import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, FolderKanban, LogOut, Bell, Users, ShoppingBag, Inbox, Receipt, Sparkles, Settings as SettingsIcon, ChevronRight, Headset, UserCircle,
} from "lucide-react";
import { billing } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { getPortalUnread } from "@/lib/portalApi";
import { StaffAlerts } from "@/components/portal/StaffAlerts";
import { ErpThemeProvider, useErpTheme, assetUrl } from "@/context/ErpThemeContext";

const SECTIONS = {
  ops: { en: "Operations", pt: "Operações", fr: "Opérations" },
  fin: { en: "Finance", pt: "Finanças", fr: "Finances" },
  ai: { en: "Intelligence", pt: "Inteligência", fr: "Intelligence" },
  admin: { en: "Administration", pt: "Administração", fr: "Administration" },
};
const SUPPORT_LABEL = { en: "Support & sales", pt: "Apoio e comercial", fr: "Assistance et ventes" };
const PROFILE_LABEL = { en: "My profile", pt: "O meu perfil", fr: "Mon profil" };
const SETTINGS_LABEL = { en: "Settings", pt: "Definições", fr: "Paramètres" };

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();

const Badge = ({ n, tone = "green", testid }) =>
  n > 0 ? (
    <span data-testid={testid} className={`erp-badge ${tone === "amber" ? "bg-amber-400 text-slate-950" : "bg-venka-green text-white"}`}>{n > 99 ? "99+" : n}</span>
  ) : null;

const Shell = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const { user, logout, isAdmin } = useAuth();
  const { branding } = useErpTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { bt } = useBT();
  const [unread, setUnread] = useState(0);
  const [newApps, setNewApps] = useState(0);
  const [billingCount, setBillingCount] = useState(0);
  const [supportCount, setSupportCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try { const d = await getPortalUnread(); if (alive) setUnread(d?.total || 0); } catch (e) { /* ignore */ }
      try {
        const token = localStorage.getItem("venka_token");
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/erp/applications?status=new&limit=1`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (r.ok) { const j = await r.json(); if (alive) setNewApps(j?.total || 0); }
      } catch (e) { /* ignore */ }
      try { const b = await billing.badge(); if (alive) setBillingCount(b?.count || 0); } catch (e) { /* ignore */ }
      try {
        const token = localStorage.getItem("venka_token");
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/support/summary`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (r.ok) { const j = await r.json(); if (alive) setSupportCount(j?.unread || 0); }
      } catch (e) { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const L = (o) => o[lang] || o.en;
  const groups = useMemo(() => [
    { key: "ops", items: [
      { to: "/portal", end: true, icon: LayoutDashboard, label: t("portal.nav.dashboard"), testid: "portal-nav-dashboard" },
      { to: "/portal/processes", icon: FolderKanban, label: t("portal.nav.processes"), badge: unread, testid: "portal-nav-processes", badgeId: "portal-unread-badge" },
      { to: "/portal/applications", icon: Inbox, label: t("portal.nav.applications", "Applications"), badge: newApps, testid: "portal-nav-applications", badgeId: "portal-new-apps-badge" },
      { to: "/portal/support", icon: Headset, label: L(SUPPORT_LABEL), badge: supportCount, testid: "portal-nav-support", badgeId: "portal-support-badge" },
    ] },
    { key: "fin", items: [
      { to: "/portal/billing", icon: Receipt, label: bt("billing"), badge: billingCount, tone: "amber", testid: "portal-nav-billing", badgeId: "portal-billing-badge" },
      { to: "/portal/orders", icon: ShoppingBag, label: t("portal.nav.orders", "Package Orders"), testid: "portal-nav-orders" },
    ] },
    { key: "ai", items: [
      { to: "/portal/ai", icon: Sparkles, label: t("portal.nav.ai", "AI agents"), testid: "portal-nav-ai" },
    ] },
    { key: "admin", items: [
      ...(isAdmin ? [{ to: "/portal/users", icon: Users, label: t("portal.nav.team", "Team & Customers"), testid: "portal-nav-users" }] : []),
      { to: "/portal/notifications", icon: Bell, label: t("portal.nav.notifications"), testid: "portal-nav-notifications" },
      { to: "/portal/profile", icon: UserCircle, label: L(PROFILE_LABEL), testid: "portal-nav-profile" },
      ...(isAdmin ? [{ to: "/portal/settings", icon: SettingsIcon, label: L(SETTINGS_LABEL), testid: "portal-nav-settings" }] : []),
    ] },
  ], [t, bt, unread, newApps, billingCount, supportCount, isAdmin, lang]); // eslint-disable-line

  const flat = groups.flatMap((g) => g.items.map((it) => ({ ...it, section: g.key })));
  const current = [...flat].sort((a, b) => b.to.length - a.to.length)
    .find((it) => (it.end ? location.pathname === it.to : location.pathname.startsWith(it.to))) || flat[0];

  const logoSrc = assetUrl(branding.logo_url) || "/venka-logo.png";
  const handleLogout = () => { logout(); navigate("/portal/login", { replace: true }); };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Sidebar — fixed while the page scrolls */}
      <aside className="erp-dark-zone erp-sidebar hidden md:flex w-[264px] shrink-0 flex-col sticky top-0 h-screen text-slate-200" data-testid="portal-sidebar">
        <div className="px-5 pt-5 pb-4">
          <div className="erp-logo-tile">
            <img src={logoSrc} alt={branding.app_name} className="h-11 w-auto mx-auto" data-testid="portal-logo" />
          </div>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-venka-green font-semibold">{branding.erp_title}</p>
        </div>
        <nav className="erp-scroll flex-1 overflow-y-auto px-3 pb-4">
          {groups.map((g) => g.items.length > 0 && (
            <div key={g.key}>
              <p className="erp-nav-section">{L(SECTIONS[g.key])}</p>
              <div className="flex flex-col gap-1">
                {g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `erp-nav-link ${isActive ? "active" : ""}`} data-testid={it.testid}>
                    <span className="flex items-center gap-3"><it.icon className="h-[18px] w-[18px]" />{it.label}</span>
                    <Badge n={it.badge} tone={it.tone} testid={it.badgeId} />
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="m-3 rounded-xl bg-white/5 p-3 flex items-center gap-3">
          <Link to="/portal/profile" title={L(PROFILE_LABEL)} className="shrink-0">
            {user?.avatar_url ? <img src={user.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
              : <span className="grid h-9 w-9 place-items-center rounded-full bg-venka-blue text-sm font-bold text-white">{initials(user?.name)}</span>}
          </Link>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white" data-testid="portal-current-user">{user?.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-venka-green">{user?.role}</p>
          </div>
          <button type="button" onClick={handleLogout} title={t("portal.nav.logout")} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/15 hover:text-red-300" data-testid="portal-logout-button">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 glass border-b border-white/10" data-testid="portal-header">
          <div className="h-16 flex items-center gap-3 px-4 md:px-8">
            <img src={logoSrc} alt="" className="md:hidden h-8 w-auto rounded bg-white px-1" />
            <div className="min-w-0">
              <p className="hidden sm:flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-500">
                {L(SECTIONS[current.section])} <ChevronRight className="h-3 w-3" />
              </p>
              <p className="truncate text-base md:text-lg font-semibold text-slate-100" data-testid="portal-page-title">{current.label}</p>
            </div>
            <div className="ml-auto flex items-center gap-2 md:gap-3">
              <StaffAlerts />
              <LanguageSelector />
              <Link to="/portal/profile" title={L(PROFILE_LABEL)} className="hidden md:block" data-testid="portal-header-profile">
                {user?.avatar_url ? <img src={user.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                  : <span className="grid h-9 w-9 place-items-center rounded-full bg-venka-blue/10 text-sm font-bold text-venka-blue">{initials(user?.name)}</span>}
              </Link>
              <button type="button" onClick={handleLogout} className="md:hidden text-slate-400 hover:text-red-400" data-testid="portal-logout-mobile">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
          <nav className="md:hidden flex gap-1 overflow-x-auto border-t border-white/10 px-2 py-2" data-testid="portal-mobile-nav">
            {flat.map((it) => (
              <NavLink key={it.to} to={it.to} end={!!it.end}
                className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-venka-blue text-white" : "text-slate-400"}`}>
                {it.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 px-4 py-6 md:px-8 md:py-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const PortalLayout = () => (
  <ErpThemeProvider>
    <Shell />
  </ErpThemeProvider>
);

export default PortalLayout;
